import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  InvokeOptions,
  SectionInstruction,
} from './useCompositeReportGeneration';

const useStyles = makeStyles(theme => ({
  textArea: {
    resize: 'none',
    height: theme.pxToRem(80),
  },
}));

const messages = defineMessages({
  regenerate: {
    id: 'CompositeReport.SectionRegenerateButton.regenerate',
    defaultMessage: 'Regenerate',
  },
  cancelLabel: {
    id: 'CompositeReport.SectionRegenerateButton.cancel',
    defaultMessage: 'Cancel',
  },
  confirmLabel: {
    id: 'CompositeReport.SectionRegenerateButton.confirm',
    defaultMessage: 'Regenerate',
  },
  warningTitle: {
    id: 'CompositeReport.SectionRegenerateButton.warningTitle',
    defaultMessage: 'Regenerate section?',
  },
  warningBody: {
    id: 'CompositeReport.SectionRegenerateButton.warningBody',
    defaultMessage:
      'This section has been manually edited. Regenerating will replace your edits. Continue?',
  },
  instructionsLabel: {
    id: 'CompositeReport.SectionRegenerateButton.instructionsLabel',
    defaultMessage: 'Instructions (optional)',
  },
  instructionsPlaceholder: {
    id: 'CompositeReport.SectionRegenerateButton.instructionsPlaceholder',
    defaultMessage:
      'Enter any additional instructions for regeneration (e.g., "Output in Spanish").',
  },
});

export type SectionRegenerateButtonProps = {
  /** The template durable ID for this section — passed directly to the API. */
  templateId: string;
  /** ID of the composite report definition (CRD) used to invoke the agent. */
  crdId: string;
  /**
   * Whether this section was last saved by a human (is_ai_generated === false).
   * When true and no instructions are provided, a confirmation dialog is shown.
   */
  isHumanEdited: boolean;
  /**
   * Callback invoked after `invoke()` resolves (success or conflict).
   * Callers can use this to trigger a CarePlan refetch.
   */
  onRegenerate?: () => void;

  // ── Controlled state from the parent's shared hook ──────────────────────
  //
  // The parent renders ONE useCompositeReportGeneration instance and passes its
  // state here so that:
  // (a) All sibling section buttons are disabled the instant any one invokes.
  // (b) Only one polling interval runs per subject (not one per button).
  // (c) Errors are surfaced to the caller for UI feedback.
  /** The `invoke` function from `useCompositeReportGeneration`. */
  invoke: (crdId: string, opts?: InvokeOptions) => Promise<void>;
  /** True while the HTTP invoke request is in-flight (local to the parent hook). */
  isInvoking: boolean;
  /** True while the parent hook is polling for a running task. */
  isPolling: boolean;
  /** Error from the last failed invoke attempt, if any. */
  error?: Error;
};

/**
 * Per-section regeneration button for the Composite Report module (CR-10).
 *
 * This is a **controlled** component — it receives `invoke`, `isInvoking`,
 * `isPolling`, and `error` from the parent, which owns the single shared
 * `useCompositeReportGeneration` instance for the whole section list.
 * This ensures:
 * - Sibling buttons are all disabled the moment any one invokes.
 * - Only one polling interval runs per subject.
 * - Errors from any section regen are visible to the caller.
 *
 * Behaviour:
 * - AI-generated section → immediately invokes with subset payload.
 * - Human-edited + no instructions → warning dialog before invoke.
 * - Human-edited + instructions → skips warning (explicit intent).
 * - Disabled while `isInvoking || isPolling`.
 */
export const SectionRegenerateButton: React.FC<
  SectionRegenerateButtonProps
> = ({
  templateId,
  crdId,
  isHumanEdited,
  onRegenerate,
  invoke,
  isInvoking,
  isPolling,
  error,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const [instructions, setInstructions] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [invokeError, setInvokeError] = useState<Error | undefined>(undefined);

  const hasInstructions = instructions.trim().length > 0;
  const isDisabled = isInvoking || isPolling;

  const doInvoke = async () => {
    if (isDisabled) return;
    setInvokeError(undefined);
    const invokeOpts: InvokeOptions = {
      sectionsToGenerate: {
        type: 'subset' as const,
        subset: [{ template_id: templateId }],
      },
      instructions: hasInstructions
        ? ([
            { template_id: templateId, instructions: instructions.trim() },
          ] as SectionInstruction[])
        : undefined,
    };

    try {
      await invoke(crdId, invokeOpts);
      setInstructions('');
      onRegenerate?.();
    } catch (err) {
      setInvokeError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  const handleClick = () => {
    if (isHumanEdited && !hasInstructions) {
      setShowWarning(true);
      return;
    }
    doInvoke();
  };

  const handleWarningConfirm = () => {
    setShowWarning(false);
    // Re-check disabled state: the parent may have set isInvoking/isPolling
    // while the warning dialog was open (e.g. another section started regen).
    if (isDisabled) return;
    doInvoke();
  };

  const handleWarningCancel = () => {
    setShowWarning(false);
    // Instructions state is intentionally preserved so the user can add
    // instructions and retry without retyping them.
  };

  const displayError = invokeError ?? error;

  return (
    <>
      <Box direction="column" gap={1}>
        <TextArea
          label={formatMessage(messages.instructionsLabel)}
          placeholder={formatMessage(messages.instructionsPlaceholder)}
          value={instructions}
          onChange={e => setInstructions(e.target.value)}
          className={classes.textArea}
          fullWidth
          aria-label={formatMessage(messages.instructionsLabel)}
        />
        <Button
          onClick={handleClick}
          disabled={isDisabled}
          data-testid="section-regenerate-button"
        >
          {formatMessage(messages.regenerate)}
        </Button>
        {displayError && (
          <Text size="body" color="negative" data-testid="section-regen-error">
            {displayError.message}
          </Text>
        )}
      </Box>

      <Modal
        title={formatMessage(messages.warningTitle)}
        isOpen={showWarning}
        onDismiss={handleWarningCancel}
        size={0}
        actions={
          <>
            <Button
              onClick={handleWarningCancel}
              variant="outlined"
              color="negative"
              fullWidth
            >
              {formatMessage(messages.cancelLabel)}
            </Button>
            <Button
              onClick={handleWarningConfirm}
              fullWidth
              data-testid="section-regenerate-confirm"
            >
              {formatMessage(messages.confirmLabel)}
            </Button>
          </>
        }
      >
        <Text size="body">{formatMessage(messages.warningBody)}</Text>
      </Modal>
    </>
  );
};
